import { useState, useEffect, useCallback } from 'react';
import { 
  checkBackendHealth, 
  getBackendHealthStatus, 
  subscribeToHealthChanges,
  waitForBackendHealth,
  isBackendReady 
} from '../utils/backendHealth';

/**
 * Hook pour surveiller et utiliser l'état de santé du backend
 * Permet aux composants de réagir aux changements de disponibilité du backend
 */
export function useBackendHealth() {
  const [isHealthy, setIsHealthy] = useState<boolean>(isBackendReady());
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [status, setStatus] = useState<'unknown' | 'healthy' | 'unhealthy'>(getBackendHealthStatus());

  // S'abonner aux changements d'état de santé
  useEffect(() => {
    const unsubscribe = subscribeToHealthChanges((healthy) => {
      setIsHealthy(healthy);
      setStatus(healthy ? 'healthy' : 'unhealthy');
    });

    return unsubscribe;
  }, []);

  // Vérifier la santé au montage du composant
  useEffect(() => {
    const checkHealth = async () => {
      setIsChecking(true);
      try {
        const healthy = await checkBackendHealth();
        setIsHealthy(healthy);
        setStatus(healthy ? 'healthy' : 'unhealthy');
      } finally {
        setIsChecking(false);
      }
    };

    // Ne vérifier que si le statut est inconnu
    if (status === 'unknown') {
      checkHealth();
    }
  }, []); // Une seule fois au montage

  // Fonction pour forcer une vérification manuelle
  const checkHealth = useCallback(async () => {
    setIsChecking(true);
    try {
      const healthy = await checkBackendHealth();
      setIsHealthy(healthy);
      setStatus(healthy ? 'healthy' : 'unhealthy');
      return healthy;
    } finally {
      setIsChecking(false);
    }
  }, []);

  // Fonction pour attendre que le backend soit prêt
  const waitForHealth = useCallback(async (maxAttempts = 3, delayMs = 2000) => {
    setIsChecking(true);
    try {
      const healthy = await waitForBackendHealth(maxAttempts, delayMs);
      setIsHealthy(healthy);
      setStatus(healthy ? 'healthy' : 'unhealthy');
      return healthy;
    } finally {
      setIsChecking(false);
    }
  }, []);

  return {
    isHealthy,
    isChecking,
    status,
    checkHealth,
    waitForHealth,
  };
}

export default useBackendHealth;
